import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, 
  FileText, 
  Megaphone, 
  Wrench, 
  AlertTriangle,
  Building,
  LogOut,
  Menu,
  X,
  Package,
  Calendar,
  DollarSign,
  Users,
  UserCog,
  Shield,
  ClipboardList,
  UserPlus,
  Building2,
  FolderOpen,
  Activity
} from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OnboardingDialog from './OnboardingDialog';
import GlobalSearch from './GlobalSearch';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, selectedBuilding, setSelectedBuilding } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await axios.get(`${API}/buildings`);
        const data = response.data;
        setBuildings(data);
        // Auto-select single building for data isolation
        if (data.length === 1 && selectedBuilding === 'all') {
          setSelectedBuilding(data[0].id);
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      }
    };
    if (user) fetchBuildings();
  }, [user]);

  // Fetch global stats for super_admin monitor
  useEffect(() => {
    const fetchGlobalStats = async () => {
      if (user?.role !== 'super_admin') return;
      try {
        const [bookingsRes, maintenanceRes, pqrsRes] = await Promise.all([
          axios.get(`${API}/bookings?include_cancelled=false`),
          axios.get(`${API}/maintenance`),
          axios.get(`${API}/pqrs`)
        ]);
        setGlobalStats({
          activeBookings: bookingsRes.data.filter(b => b.status === 'confirmed').length,
          pendingMaintenance: maintenanceRes.data.filter(m => m.status !== 'completed' && m.status !== 'cancelled').length,
          openPQRS: pqrsRes.data.filter(p => p.status !== 'resolved' && p.status !== 'closed').length
        });
      } catch (error) {
        console.error('Error fetching global stats:', error);
      }
    };
    fetchGlobalStats();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard',    href: '/dashboard',       icon: Home,       roles: ['admin', 'resident', 'staff'] },
    { name: 'Mis Conjuntos',href: '/admin/conjuntos',  icon: Building,   roles: ['admin'] },
    { name: 'Residentes',   href: '/residents',        icon: Users,      roles: ['admin', 'staff'] },
    { name: 'Usuarios',     href: '/admin/usuarios',   icon: UserCog,    roles: ['admin'] },
    { name: 'Documentos',   href: '/documents',        icon: FolderOpen, roles: ['admin', 'super_admin'] },
    { name: 'Reservas',     href: '/bookings',         icon: Calendar,   roles: ['admin', 'resident'] },
    { name: 'Paquetería',   href: '/packages',         icon: Package,    roles: ['admin', 'resident', 'staff'] },
    { name: 'Finanzas',     href: '/finances',         icon: DollarSign, roles: ['admin'] },
    { name: 'PQRS',         href: '/pqrs',             icon: FileText,   roles: ['admin', 'resident'] },
    { name: 'Avisos',       href: '/announcements',    icon: Megaphone,  roles: ['admin', 'resident'] },
    { name: 'Mantenimiento',href: '/maintenance',      icon: Wrench,     roles: ['admin', 'staff'] },
    { name: 'Emergencias',  href: '/emergencies',      icon: AlertTriangle, roles: ['admin', 'resident', 'staff'] },
  ];

  // Super Admin navigation
  const superAdminNavigation = [
    { name: 'Panel Super Admin', href: '/super-admin/dashboard', icon: Shield },
    { name: 'Solicitudes',       href: '/super-admin/solicitudes', icon: ClipboardList },
    { name: 'Invitar Admin',     href: '/super-admin/invitar-admin', icon: UserPlus },
    { name: 'Todos los Conjuntos', href: '/super-admin/conjuntos', icon: Building2 },
    { name: 'Todos los Usuarios', href: '/super-admin/usuarios', icon: Users },
  ];

  const filteredNavigation = navigation.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full w-64 bg-primary text-white z-50
          transform transition-transform duration-300 ease-out shadow-2xl
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 border-r border-white/5
        `}
      >
        <div className="flex flex-col h-full bg-gradient-to-b from-primary to-[#1a1b36]">
          {/* Logo */}
          <div className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/10 p-2 rounded-xl backdrop-blur-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
                  alt="LivCom" 
                  className="h-6 w-6"
                />
              </div>
              <h1 className="text-xl font-heading font-bold tracking-tight text-white">LivCom</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-6 right-6 lg:hidden text-white/70 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* User info - clickable to profile */}
          <div className="px-6 pb-6">
            <Link
              to="/perfil"
              className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors cursor-pointer"
              onClick={() => setSidebarOpen(false)}
              data-testid="profile-link-sidebar"
            >
              <div className="h-10 w-10 rounded-xl bg-secondary/20 flex items-center justify-center text-secondary font-bold border border-secondary/20 overflow-hidden shrink-0">
                {user?.photo_url
                  ? <img src={user.photo_url} alt="avatar" className="h-full w-full object-cover" />
                  : <span>{user?.name?.charAt(0).toUpperCase()}</span>
                }
              </div>
              <div className="overflow-hidden flex-1 min-w-0">
                <p className="font-medium text-sm text-white truncate">{user?.name}</p>
                <p className="text-xs text-white/50 capitalize truncate">{user?.role}</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto custom-scrollbar">
            {/* Super Admin Section */}
            {user?.role === 'super_admin' && (
              <>
                <p className="px-4 py-2 text-xs font-semibold text-white/30 uppercase tracking-widest">Super Admin</p>
                {superAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-super-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                        transition-all duration-200 group
                        ${isActive 
                          ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' 
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
                
                {/* Monitor Global - Solo lectura */}
                {globalStats && (
                  <div className="mt-4 mx-2 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="h-4 w-4 text-orange-400" />
                      <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Monitor Global</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50">Reservas activas</span>
                        <span className="text-sm font-bold text-orange-400">{globalStats.activeBookings}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50">Mant. pendientes</span>
                        <span className="text-sm font-bold text-orange-400">{globalStats.pendingMaintenance}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50">PQRS abiertos</span>
                        <span className="text-sm font-bold text-orange-400">{globalStats.openPQRS}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="my-4 border-t border-white/10" />
              </>
            )}
            
            <p className="px-4 py-2 text-xs font-semibold text-white/30 uppercase tracking-widest">Menú Principal</p>
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                    transition-all duration-200 group
                    ${isActive 
                      ? 'bg-secondary text-primary shadow-lg shadow-secondary/20' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-primary' : 'text-slate-500 group-hover:text-white'}`} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5 bg-black/20">
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-3 px-4 py-3 rounded-xl w-full text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all group"
            >
              <LogOut className="h-5 w-5 group-hover:text-red-400 transition-colors" />
              <span className="font-medium text-sm">Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen transition-all duration-300">
        <OnboardingDialog />
        
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-slate-200/50">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-slate-500 hover:text-primary transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-6 w-6" />
              </button>

              {/* Global Search */}
              <GlobalSearch />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <Building className="h-4 w-4 text-secondary" />
                <div className="w-[200px] sm:w-[240px]">
                  <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                    <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-transparent focus:ring-0 p-0 h-auto font-medium text-sm text-slate-700">
                      <SelectValue placeholder="Seleccionar conjunto" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-100 shadow-xl rounded-xl">
                      {buildings.length !== 1 && (
                        <SelectItem value="all" className="rounded-lg">Todos los conjuntos</SelectItem>
                      )}
                      {buildings.map((building) => (
                        <SelectItem key={building.id} value={building.id} className="rounded-lg">
                          {building.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;